import React from 'react';
import Joi from 'joi';
import PropTypes from 'prop-types';

import Button from '../../themed/button';
import Form from '../../common/form';
import ListGroup from '../../blocks/list-group';
import VIObservationItemForm from './viObservationItemForm';
import VIRecommendationItemForm from './viRecommendationItemForm';

import { MAX_IMAGES_PER_ITEM } from '../../../utils/constants';
import { getRiskLevelsAsOption } from '../../../utils/data';
import { getFileUploadTypes } from '../../../utils/utilities';
import { SelectOption, VIReportItem } from '../../../utils/models';

interface VIReportDataItemFormProps {
	reportItem: VIReportItem;
	onEdit: (item: VIReportItem) => void;
	onCancelEdit: () => void;
}

class EditVIReportDataItemForm extends Form {
	state = {
		data: initialVIData,
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		id: Joi.number().required().label('S/N'),
		tagNumber: Joi.string()
			.min(3)
			.required()
			.label('Identification / Tag Number'),
		observations: Joi.array()
			.min(1)
			.items(
				Joi.object({
					id: Joi.number().required().label('S/N'),
					name: Joi.string().min(3).required().label('Observation / Result'),
				})
			)
			.required()
			.label('Observation / Result'),
		recommendations: Joi.array()
			.min(1)
			.items(
				Joi.object({
					id: Joi.number().required().label('S/N'),
					name: Joi.string().min(3).required().label('Observation / Result'),
				})
			)
			.required()
			.label('Recommendation / Action'),
		level: Joi.string().required().label('Criticality'),
		images: Joi.array().min(1).required().label('Anomaly Image'),
	};

	static propTypes = {
		reportItem: PropTypes.object.isRequired,
		onEdit: PropTypes.func,
	};

	componentDidMount() {
		const item: VIReportItem | null = this.props.reportItem;
		if (item) this.setState({ data: item });
	}

	handleAddObservation = (name: string) => {
		const data = { ...this.state.data };
		data.observations.push({ id: data.observations.length + 1, name });

		this.setState({ data });
	};

	handleAddRecommendation = (name: string) => {
		const data = { ...this.state.data };
		data.recommendations.push({ id: data.recommendations.length + 1, name });

		this.setState({ data });
	};

	handleDeleteObservation = ({ id }: SelectOption) => {
		const data = { ...this.state.data };
		data.observations = data.observations.filter((o) => o.id !== id);

		this.setState({ data });
	};

	handleDeleteRecommendation = ({ id }: SelectOption) => {
		const data = { ...this.state.data };
		data.recommendations = data.recommendations.filter((o) => o.id !== id);

		this.setState({ data });
	};

	doSubmit = (): void => {
		const props = this.props as VIReportDataItemFormProps;
		props.onEdit(this.state.data);

		this.setState({ data: initialVIData });
	};

	render() {
		return (
			<React.Fragment>
				<form autoComplete="off" onSubmit={this.handleSubmit}>
					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'tagNumber',
								placeholder: 'Enter Identification / Tag Number',
								label: 'Identification / Tag Number',
							})}
						</div>

						<div className="col">
							{this.renderSelect({
								name: 'level',
								placeholder: 'Select Criticality',
								label: 'Criticality',
								options: getRiskLevelsAsOption(),
							})}
						</div>
					</div>

					<div className="divider my-4"></div>

					<div className="row">
						<div className="col">
							<VIObservationItemForm
								onAddObservation={this.handleAddObservation}
							/>

							<ListGroup
								data={this.state.data.observations}
								onRemove={this.handleDeleteObservation}
							/>
						</div>

						<div className="col">
							<VIRecommendationItemForm
								onAddRecommendation={this.handleAddRecommendation}
							/>

							<ListGroup
								data={this.state.data.recommendations}
								onRemove={this.handleDeleteRecommendation}
							/>
						</div>
					</div>

					<div className="divider my-4"></div>

					{this.renderFileInput({
						max: MAX_IMAGES_PER_ITEM,
						multiple: true,
						name: 'images',
						accept: 'image/*,application/pdf',
						label: 'Anomaly Image',
						sublabel: 'Upload photos showing anomaly',
						types: getFileUploadTypes(),
					})}

					<div className="d-flex justify-content-start">
						{this.renderButton('Update')}

						<div className="mx-4">
							<Button
								label="Cancel"
								style={{ backgroundColor: 'red' }}
								onClick={this.props.onCancelEdit}
							/>
						</div>
					</div>
				</form>
			</React.Fragment>
		);
	}
}

const initialVIData: VIReportItem = {
	id: 0,
	tagNumber: '',
	observations: [],
	recommendations: [],
	level: 'minor',
	images: [],
};

export default EditVIReportDataItemForm;
